import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface BuildInfo {
  version: string;
  buildDate: string;
  buildNumber: string;
  environment: string;
  commitHash: string;
  nodeVersion: string;
  reactVersion: string;
}

const buildInfo: BuildInfo = {
  version: import.meta.env.VITE_APP_VERSION || '1.0.0',
  buildDate: import.meta.env.VITE_BUILD_DATE || new Date().toISOString(),
  buildNumber: import.meta.env.VITE_BUILD_NUMBER || 'dev',
  environment: import.meta.env.MODE || 'development',
  commitHash: import.meta.env.VITE_COMMIT_HASH || 'unknown',
  nodeVersion: import.meta.env.VITE_NODE_VERSION || 'unknown',
  reactVersion: React.version,
};

export default function InfoPage() {
  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const [apiStatus, setApiStatus] = useState<'idle' | 'ok' | 'error' | 'loading'>('loading');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    let mounted = true;
    const fetchStatus = async () => {
      setApiStatus(prev => prev === 'loading' ? 'loading' : 'loading');
      try {
        const res = await fetch('/api/health');
        if (!mounted) return;
        if (res.ok) {
          setApiStatus('ok');
          setErrorMessage('');
        } else {
          setApiStatus('error');
          setErrorMessage(`Status ${res.status}`);
        }
      } catch {
        if (!mounted) return;
        setApiStatus('error');
        setErrorMessage('Network error');
      } finally {
        if (mounted) setLastUpdated(new Date());
      }
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []);

  return (
    <div className="px-4">
      {/* Breadcrumb Navigation */}
      <nav className="flex mb-4" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li className="inline-flex items-center">
            <Link
              to="/admin/management"
              className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              Management
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
              <span className="ml-1 md:ml-2 text-sm font-medium text-gray-500 dark:text-gray-400">System Information</span>
            </div>
          </li>
        </ol>
      </nav>

      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">System Information</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          Build details and system information
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Build Information */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg dark:border dark:border-gray-700">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 mb-4">
              Build Information
            </h3>
            <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Version</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100 font-mono">
                  {buildInfo.version}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Build Date</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                  {formatDate(buildInfo.buildDate)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Build Number</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100 font-mono">
                  {buildInfo.buildNumber}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Environment</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    buildInfo.environment === 'production' 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' 
                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
                  }`}>
                    {buildInfo.environment}
                  </span>
                </dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Commit Hash</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100 font-mono break-all">
                  {buildInfo.commitHash}
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Runtime Information */}
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg dark:border dark:border-gray-700">
          <div className="px-4 py-5 sm:p-6">
      <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 mb-4">
              Runtime Information
            </h3>
            <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
              <div>
        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">React Version</dt>
        <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100 font-mono">
                  {buildInfo.reactVersion}
                </dd>
              </div>
              <div>
        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Node Version</dt>
        <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100 font-mono">
                  {buildInfo.nodeVersion}
                </dd>
              </div>
              <div>
        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">User Agent</dt>
        <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100 break-all">
                  {navigator.userAgent}
                </dd>
              </div>
              <div>
        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Browser Language</dt>
        <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                  {navigator.language}
                </dd>
              </div>
              <div>
        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Screen Resolution</dt>
        <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                  {screen.width} × {screen.height}
                </dd>
              </div>
              <div>
        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Timezone</dt>
        <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                  {Intl.DateTimeFormat().resolvedOptions().timeZone}
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {/* System Status */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg dark:border dark:border-gray-700 lg:col-span-2">
          <div className="px-4 py-5 sm:p-6">
            <div className="mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                System Status
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Frontend</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Online</p>
                </div>
              </div>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className={`w-3 h-3 rounded-full ${apiStatus === 'loading' ? 'bg-yellow-400 animate-pulse' : apiStatus === 'ok' ? 'bg-green-400' : 'bg-red-500'}`}></div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">API Connection</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {apiStatus === 'loading' && 'Checking...'}
                    {apiStatus === 'ok' && 'Connected'}
                    {apiStatus === 'error' && (errorMessage || 'Error')}
                  </p>
                </div>
              </div>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Last Updated</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{lastUpdated ? lastUpdated.toLocaleTimeString() : '—'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}